import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { getJobMediaError, setJobMedia } from "./media.actions";
import MediaActionTypes from "./media.types";
import cleanAxios from "../../utils/CleanAxios";
import normalizeUrl from "normalize-url";

const normalizeMediaTags = (mediaTags) => [
  ...new Set(
    (Array.isArray(mediaTags) ? mediaTags : [])
      .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
      .filter(Boolean)
  )
];

const extractMediaTags = (payload) => {
  const candidates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.mediaTags)
      ? payload.mediaTags
      : Array.isArray(payload?.tags)
        ? payload.tags
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

  return candidates
    .map((tag) => {
      if (typeof tag === "string") return tag;
      if (tag && typeof tag === "object") {
        return tag.media_tag ?? tag.mediaTag ?? tag.tag ?? tag.name ?? "";
      }
      return "";
    })
    .filter(Boolean);
};

const mergeMediaTags = (bodyshopTags, serverTags) => [
  ...new Set([...normalizeMediaTags(bodyshopTags), ...normalizeMediaTags(serverTags)])
];

const normalizeMediaResponse = (data, localmediaserverhttp) => {
  const responseItems = Array.isArray(data) ? data : Array.isArray(data?.media) ? data.media : [];

  return {
    items: responseItems.map((d, idx) => ({
      ...d,
      src: normalizeUrl(`${localmediaserverhttp}/${d.src}`),
      thumbnail: normalizeUrl(`${localmediaserverhttp}/${d.thumbnail}`),
      ...(d.optimized && {
        optimized: normalizeUrl(`${localmediaserverhttp}/${d.optimized}`)
      }),
      isSelected: false,
      key: idx
    })),
    mediaTags: []
  };
};

const getMediaTags = async (bodyshop, localmediaserverhttp, jobid) => {
  try {
    const response = await cleanAxios.post(
      `${localmediaserverhttp}/media/tags`,
      { jobid },
      {
        headers: { ims_token: bodyshop.localmediatoken, bodyshopid: bodyshop.id }
      }
    );
    const mediaTags = extractMediaTags(response.data);
    return mediaTags.length > 0
      ? mergeMediaTags(bodyshop.media_tags, mediaTags)
      : normalizeMediaTags(bodyshop.media_tags);
  } catch {
    return normalizeMediaTags(bodyshop.media_tags);
  }
};

export function* onSetJobMedia() {
  yield takeLatest(MediaActionTypes.GET_MEDIA_FOR_JOB, getJobMedia);
}

export function* getJobMedia({ payload: jobid }) {
  try {
    const bodyshop = yield select((state) => state.user.bodyshop);
    const localmediaserverhttp = bodyshop.localmediaserverhttp.trim();

    if (localmediaserverhttp && localmediaserverhttp !== "") {
      const [imagesFetch, documentsFetch, mediaTags] = yield all([
        cleanAxios.post(
          `${localmediaserverhttp}/jobs/list`,
          {
            jobid
          },
          { headers: { ims_token: bodyshop.localmediatoken, bodyshopid: bodyshop.id } }
        ),
        cleanAxios.post(
          `${localmediaserverhttp}/bills/list`,
          {
            jobid
          },
          { headers: { ims_token: bodyshop.localmediatoken } }
        ),
        call(getMediaTags, bodyshop, localmediaserverhttp, jobid)
      ]);

      const images = normalizeMediaResponse(imagesFetch.data, localmediaserverhttp);
      const documents = normalizeMediaResponse(documentsFetch.data, localmediaserverhttp);

      yield put(
        setJobMedia({
          jobid,
          media: [...images.items, ...documents.items],
          mediaTags
        })
      );
    }
  } catch (error) {
    yield put(getJobMediaError({ jobid, error }));
  }
}

export function* onSetBillMedia() {
  yield takeLatest(MediaActionTypes.GET_MEDIA_FOR_BILL, getBillMedia);
}

export function* getBillMedia({ payload: { jobid, invoice_number } }) {
  try {
    const bodyshop = yield select((state) => state.user.bodyshop);
    const localmediaserverhttp = bodyshop.localmediaserverhttp.trim();

    if (localmediaserverhttp && localmediaserverhttp !== "") {
      const [documentsFetch, mediaTags] = yield all([
        cleanAxios.post(
          `${localmediaserverhttp}/bills/list`,
          {
            jobid,
            invoice_number
          },
          { headers: { ims_token: bodyshop.localmediatoken } }
        ),
        call(getMediaTags, bodyshop, localmediaserverhttp, jobid)
      ]);

      const documents = normalizeMediaResponse(documentsFetch.data, localmediaserverhttp);

      yield put(
        setJobMedia({
          jobid,
          media: documents.items,
          mediaTags
        })
      );
    }
  } catch (error) {
    yield put(getJobMediaError({ jobid, error }));
  }
}

export function* mediaSagas() {
  yield all([call(onSetJobMedia), call(onSetBillMedia)]);
}
